/**
 * Paging for a list held in full on the client.
 *
 * `usePaged` returns one page of `items`. The page snaps back to the first
 * whenever `reset` changes (a filter, a tab), and is clamped when the list
 * shrinks under it, so it never shows an empty page past the end. `Pager` is
 * the bar under the table: first, previous, a page box, next, last.
 */

import { useEffect, useState } from 'react'

export const PAGE = 20

export function usePaged<T>(items: T[], reset: unknown, size = PAGE) {
  const [page, setPage] = useState(1)
  useEffect(() => setPage(1), [reset])
  const pages = Math.max(1, Math.ceil(items.length / size))
  const current = Math.min(page, pages)
  return {
    rows: items.slice((current - 1) * size, current * size),
    page: current,
    pages,
    setPage,
    range:
      items.length > size
        ? `${(current - 1) * size + 1}–${Math.min(current * size, items.length)} of ${items.length}`
        : `${items.length}`,
  }
}

export function Pager({
  page,
  pages,
  onPage,
}: {
  page: number
  pages: number
  onPage: (page: number) => void
}) {
  if (pages <= 1) return null
  return (
    <div className="pager">
      <button type="button" className="small" disabled={page <= 1} onClick={() => onPage(1)}>
        « First
      </button>
      <button type="button" className="small" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        ‹ Previous
      </button>
      <span className="muted">
        Page{' '}
        <input
          className="num"
          aria-label="Page"
          style={{ width: 56, display: 'inline-block' }}
          value={page}
          onChange={(e) => {
            const wanted = Number(e.target.value)
            if (Number.isInteger(wanted) && wanted >= 1 && wanted <= pages) onPage(wanted)
          }}
        />{' '}
        of {pages}
      </span>
      <button
        type="button"
        className="small"
        disabled={page >= pages}
        onClick={() => onPage(page + 1)}
      >
        Next ›
      </button>
      <button
        type="button"
        className="small"
        disabled={page >= pages}
        onClick={() => onPage(pages)}
      >
        Last »
      </button>
    </div>
  )
}
